/**
 * @license
 * Copyright (c) 2022 ForgeRock. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */

import 'core-js/stable';
import _ from 'lodash';
import App from './App';
import ApplicationStore from './store/Application';
import axios from 'axios';
import BootstrapVue from 'bootstrap-vue';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import i18n from './i18n';
import Notifications from 'vue-notification';
import PromisePoly from 'es6-promise';
import router from './router';
import UserStore from './store/User';
import {
    ValidationObserver,
    ValidationProvider,
    extend,
    localize
} from 'vee-validate';
import en from 'vee-validate/dist/locale/en.json';
import * as rules from 'vee-validate/dist/rules';
import Vue from 'vue';
import { getClientById } from './lib/getClientById';

// Turn off production warning messages
Vue.config.productionTip = false;
PromisePoly.polyfill();

Vue.use(BootstrapVue);

const appInstance = new Vue({
        el: '#app',
        router,
        i18n,
        template: '<App/>',
        components: { App },
        data: {
            userStore: UserStore,
            applicationStore: ApplicationStore
        },
        watch: {
            '$i18n.locale' (newLocale) {
                this.setDirection(newLocale);
                document.documentElement.lang = newLocale;
                document.body.setAttribute('lang', newLocale);
            }
        },
        methods: {
            setDirection (locale) {
                const rtlLocales = ['ar'];
                document.body.setAttribute('dir', rtlLocales.includes(locale) ? 'rtl' : 'ltr');
            },
            getQueryStringValue (name) {
                let url = decodeURIComponent(window.location.href);
                name = name.replace(/[[]/, '\\["').replace(/[\]]/, '\\]');
                let regexS = '[\\?&]' + name + '=([^&#]*)';
                let regex = new RegExp(regexS);
                let results = regex.exec(url);
                return results == null ? null : results[1];
            }
        },
        created () {
            const queryLocale = this.getQueryStringValue('locale');
            const initialLocale = queryLocale || navigator.language || 'en';
            this.$i18n.locale = initialLocale;
            this.setDirection(initialLocale);
            document.documentElement.lang = initialLocale;
            document.body.setAttribute('lang', initialLocale);
        }
    }),

    // Ready translated locale messages
    // IDM Context default
    idmContext = window.context || '/openidm',
    setSchemaProperties = function (schema) {
        if (_.has(schema, 'data.properties')) {
            schema.data.order.forEach((propName) => {
                let prop = schema.data.properties[propName];

                if (prop) {
                // If the property is nullable type will be an array so we need to grab the first array item that is not null to determine property type
                    if (_.isArray(prop.type)) {
                        prop.isNullable = true;
                        prop.type = _.reject(prop.type, 'null')[0];
                    }
                }
            });
        }

        return schema;
    };

// Add translation capability
/*
  Basic Translation Example:

  HTML: {{ $t("pages.resources.externalResources") }}
  JS: this.$t("pages.resources.externalResources")
 */

// Router guard to check authenticated routes
router.beforeEach((to, from, next) => {
    document.body.className = '';

    if (_.has(to, 'meta.bodyClass')) {
        document.body.className = (document.body.className + to.meta.bodyClass).trim();
    }

    if (_.has(to, 'meta.authenticate') && to.meta.authenticate === true) {
        let tempHeaders = _.extend({
                'content-type': 'application/json; charset=utf-8',
                'cache-control': 'no-cache',
                'x-requested-with': 'XMLHttpRequest'
            }, {}),
            authInstance;

        if (_.isNull(UserStore.state.userId)) {
            authInstance = axios.create({
                baseURL: idmContext,
                timeout: 5000,
                headers: tempHeaders
            });

            authInstance.post('/authentication?_action=login').then((userDetails) => {
                UserStore.setUserIdAction(userDetails.data.authorization.id);
                UserStore.setManagedResourceAction(userDetails.data.authorization.component);
                UserStore.setRolesAction(userDetails.data.authorization.roles);

                // anonymous internal user should not be allowed access
                if (userDetails.data.authorization.roles.includes('internal/role/openidm-reg')) {
                    authInstance.post('/authentication?_action=logout').then(() => {
                        next({ name: 'Login' });
                    });
                }

                // Check for progressive profiling.
                if (
                    _.has(userDetails, 'data.authorization.requiredProfileProcesses') &&
                    !_.isNull(userDetails.data.authorization.requiredProfileProcesses) &&
                    userDetails.data.authorization.requiredProfileProcesses.length > 0
                ) {
                    let profileProcess = userDetails.data.authorization.requiredProfileProcesses[0].split('/')[1];

                    next(`/profileCompletion/${profileProcess}`);
                } else {
                    axios.all([
                        authInstance.get(`${userDetails.data.authorization.component}/${userDetails.data.authorization.id}`),
                        authInstance.post(`privilege?_action=listPrivileges`),
                        authInstance.get(`schema/${userDetails.data.authorization.component}`)]).then(axios.spread((profile, privilege, schema) => {
                        const scrubbedSchema = setSchemaProperties(schema);
                        UserStore.setProfileAction(profile.data);
                        UserStore.setSchemaAction(scrubbedSchema.data);
                        UserStore.setAccess(privilege.data);

                        next();
                    }));
                }
            },
            () => {
                // Recheck class in case of double login load using from location
                document.body.className = '';

                if (_.has(from, 'meta.bodyClass')) {
                    document.body.className = (document.body.className + from.meta.bodyClass).trim();
                }

                next({ name: 'Login' });
            });
        } else {
            // anonymous internal user should not be allowed access
            if (UserStore.state.roles.includes('internal/role/openidm-reg')) {
                authInstance = axios.create({
                    baseURL: idmContext,
                    timeout: 5000,
                    headers: tempHeaders
                });

                authInstance.post('/authentication?_action=logout').then(() => {
                    next({ name: 'Login' });
                });
            } else {
                next();
            }
        }
    } else {
        next();
    }
});

router.onReady(() => {
    if (router.currentRoute?.query?.locale) {
        i18n.locale = router.currentRoute.query.locale;
        document.documentElement.lang = i18n.locale;
        document.body.setAttribute('lang', i18n.locale);
    }
});

// Globally load bootstrap vue components for use
Vue.use(BootstrapVue);
// Register validation components for global use
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
Object.keys(rules).forEach(rule => {
    extend(rule, rules[rule]);
});

// How to add an extra validation rule
extend('date_format', {
    validate (value) {
        return value.match(/^\d{2}[.//]\d{2}[.//]\d{4}$/);
    },
    message: () => {
        return 'Invalid date format';
    }
});

localize('en', en);

/*
    Basic Notification Example:
    this.$notify({
        group: 'IDMMessages', // Currently the only group
        type: 'success', // Available types success, failure, info, warning
        title: this.$t('common.messages.saveSuccess'), //Translated string
        text: this.$t('pages.resources.mappingSave') // Translated string (can also be html)
    });
 */
Vue.use(Notifications);

// Global mixin for making openIDM REST calls
Vue.mixin({
    methods: {
        // Get validation state for a form field
        getValidationState ({ dirty, validated, valid = null }) {
            return dirty || validated ? valid : null;
        },
        // Generated an axios ajax request service for consistent use of calls to IDM
        getRequestService: function (config) {
            let baseURL = idmContext,
                timeout = 0,
                headers = {
                    'content-type': 'application/json; charset=utf-8',
                    'cache-control': 'no-cache',
                    'x-requested-with': 'XMLHttpRequest',
                    'Accept-Language': i18n.locale
                },
                instance;

            if (config) {
                if (config.baseURL) {
                    baseURL = config.baseURL;
                }

                if (config.timeout) {
                    timeout = config.timeout;
                }

                if (config.headers && !_.isEmpty(config.headers)) {
                    headers = { ...headers, ...config.headers };
                }
            }

            instance = axios.create({
                baseURL: baseURL,
                timeout: timeout,
                headers: headers
            });

            instance.interceptors.response.use((response) => {
                return response;
            }, (error) => {
                if (error.response && error.response.data && error.response.data.code === 401) {
                    if (this.$route.name !== 'Login') {
                        ApplicationStore.setLoginRedirect({
                            name: this.$route.name,
                            params: this.$route.params
                        });
                    }

                    return Promise.reject(error);
                } else if (_.isUndefined(error.response)) {
                    // In the case of critical error
                    return Promise.reject(new Error(error.message));
                } else {
                    return Promise.reject(error);
                }
            });

            return instance;
        },
        // Headers used for oauth requests and selfservice
        getAnonymousHeaders: function () {
            let headers = {
                'X-OpenIDM-NoSession': true,
                'X-OpenIDM-Password': 'anonymous',
                'X-OpenIDM-Username': 'anonymous',
                'cache-control': 'no-cache'
            };

            return headers;
        },
        getSchema: function (resourcePath) {
            /* istanbul ignore next */
            let idmServiceInstance = this.getRequestService({
                headers: this.getAnonymousHeaders()
            });

            return idmServiceInstance.get(`schema/${resourcePath}`).then((schema) => {
                return setSchemaProperties(schema);
            });
        },
        // Display a application notification
        displayNotification: function (notificationType, message) {
            /* istanbul ignore next */
            this.$notify({
                group: 'IDMMessages',
                type: notificationType,
                text: message,
                ignoreDuplicates: true
            });
        },
        // Log a user out of their existing session
        logoutUser: function () {
            /* istanbul ignore next */
            let idmInstance = this.getRequestService({
                headers: this.getAnonymousHeaders()
            });
            /* istanbul ignore next */
            idmInstance.post('/authentication?_action=logout').then((response) => {
                this.$root.userStore.clearStoreAction();
                this.$router.push({ name: 'Login' });
            }, () => {
                // if an error is thrown here reloading the page will clean up the state of the app
                window.location.reload(true);
            });
        },
        // Check if progressive profile is needed
        progressiveProfileCheck (userDetails, continueLogin, updateApiType) {
            if (
                _.has(userDetails, 'data.authorization.requiredProfileProcesses') &&
                !_.isNull(userDetails.data.authorization.requiredProfileProcesses) &&
                userDetails.data.authorization.requiredProfileProcesses.length > 0
            ) {
                let profileProcess = userDetails.data.authorization.requiredProfileProcesses[0].split('/')[1];

                if (updateApiType) {
                    this.apiType = profileProcess;
                }

                this.$router.push(`/profileCompletion/${profileProcess}`);
                // If we update the apiType we need to reload the selfServiceDetails with the fresh info.
                if (updateApiType) {
                    this.loadData();
                }
            } else {
                continueLogin();
            }
        },
        // One location for checking and redirecting a completed login for s user
        completeLogin () {
            this.$router.push('/');
        },
        // Encode characters that cannot be used in browser request-header values
        encodeRFC5987IfNecessary: function (headerValue) {
            /* istanbul ignore next */
            let encoded = encodeURIComponent(headerValue)
                .replace(/['()]/g, escape)
                .replace(/\*/g, '%2A');
            /* istanbul ignore next */
            return encoded === headerValue ? headerValue : "UTF-8''" + encoded;
        }
    }
});

/*
    We will load the application regardless
 */
var startApp = function () {
    let idmInstance = axios.create({
        baseURL: idmContext,
        timeout: 5000,
        headers: {
            'X-OpenIDM-NoSession': true,
            'X-OpenIDM-Password': 'anonymous',
            'X-OpenIDM-Username': 'anonymous'
        }
    });

    ApplicationStore.setEnvironment(process.env);

    idmInstance.get('/info/uiconfig').then((uiConfigurationResponse) => {
        const uiConfig = uiConfigurationResponse.data.configuration;

        if (_.has(uiConfig, 'platformSettings.managedObjectsSettings')) {
            ApplicationStore.setManagedObjectSettings(uiConfig.platformSettings.managedObjectsSettings);
        }

        return appInstance;
    }).catch(() => appInstance);

    // Not needed for anonymous registration LCP; load after uiconfig so it does not extend critical path.
    idmInstance.get('info/features?_queryFilter=true').then((availability) => {
        ApplicationStore.setEnduserSelfservice(availability.data.result);
    }).catch(() => {});
};

startApp();

function gaEvent (eventName, eventCategory, eventAction, eventLabel) {
    if (!window.dataLayer) {
        window.dataLayer = window.dataLayer || [];
    };
    const localDt = new Date(),
        transactionLocalDt = '{year}-{month}-{date}T{hours}:{minutes}:{seconds}.{milliseconds}'
            .replace('{year}', `${localDt.getFullYear()}`)
            .replace('{month}', `00${(localDt.getMonth() + 1)}`.slice(-2))
            .replace('{date}', `00${localDt.getDate()}`.slice(-2))
            .replace('{hours}', `00${localDt.getHours()}`.slice(-2))
            .replace('{minutes}', `00${localDt.getMinutes()}`.slice(-2))
            .replace('{seconds}', `00${localDt.getSeconds()}`.slice(-2))
            .replace('{milliseconds}', `000${localDt.getMilliseconds()}`.slice(-3)),
        eventBody = {
            event: eventName,
            event_category: eventCategory,
            event_action: eventAction,
            event_label: eventLabel,
            transaction_dt: localDt.toISOString(),
            transaction_local_dt: transactionLocalDt,
            event_timestamp: localDt.getTime()
        };
    window.dataLayer.push(eventBody);
};
window.gaEvent = gaEvent;

function loadGoogleAnalyticsScript () {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/auth/googleAnalyticsId.jsp');
    xhr.addEventListener('load', () => {
        let gaId = xhr.responseText;
        const script = document.createElement('script');
        if (xhr.status !== 200 || !gaId) {
            return console.log('GTM ID is undefined.');
        }
        gaId = gaId.trim();
        const validGaIdPattern = /^(GTM|G|UA)-[A-Z0-9-]+$/;
        if (!validGaIdPattern.test(gaId)) {
            return console.error('Invalid Google Analytics/GTM ID format.');
        }
        script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gaId)}`;
        script.async = true;
        document.head.appendChild(script);
        window.dataLayer = window.dataLayer || [];
        const clientId = (getClientById() === document.location.host) ? null : getClientById();
        window.dataLayer.push({
            event: 'page_info',
            client_id: clientId || 'UNKNOWN',
            environment_code: window.location.hostname.split('.')[0].replace(/iam-?/, '').toUpperCase() || 'PROD',
            page_href: document.location.href,
            page_path: document.location.pathname,
            page_query_params: document.location.search,
            page_host: document.location.host,
            page_hash: document.location.hash,
            page_referrer: document.referrer
        });
        function gtag () {
            window.dataLayer.push(arguments);
        }
        gtag('gtm.start', new Date().getTime());
        gtag('event', 'gtm.js');
    });
    xhr.send();
};
// Performance: defer GA/GTM bootstrap until the browser is idle (or after window load)
// so it does not contend with Vue boot, dynamic chunk fetch, or the
// /selfservice/registration request that gates the LCP element. The XHR + script
// injection happens off the critical path; gaEvent itself only pushes to dataLayer
// and is safe to call before GTM finishes loading.
function scheduleGAScriptLoad () {
    var run = function () { loadGoogleAnalyticsScript(); };
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 3000 });
    } else if (document.readyState === 'complete') {
        setTimeout(run, 0);
    } else {
        window.addEventListener('load', function () { setTimeout(run, 0); }, { once: true });
    }
}
scheduleGAScriptLoad();
gaEvent('App', 'App Loaded', 'Rendered', 'openidm loaded');
